import { HttpClient, HttpEventType } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as b64toBlob from 'b64-to-blob';
import { environment } from '@environments/environment';

import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, mergeMap, retry, switchMap, take, tap, timeout } from 'rxjs/operators';

import { API_URIS } from '@shared/constants/api-uris.const';
import { MIME_TYPES } from '@shared/constants/mime-types.const';

import { PCV2Request } from '@shared/interfaces/request.interface';
import {
  Attachment,
  FileToStorage,
  PresignedUrlRequest,
  PresignedUrlResponse,
  PresignedUrlsRequest,
} from '@shared/interfaces/storage.interface';
import { DateService } from '@shared/services/date.service';
import { UploadService } from 'src/app/_upload-orders/services/upload.service';

const BASE_API_PCV2: string = environment.baseApiPCv2;
const URI_STORAGE: string = API_URIS.storage;
const URI_BUCKET_S3: string = environment.defaultBucketS3;

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  constructor(private httpClient: HttpClient, private uploadService: UploadService) {}

  // Helpers
  private assertPresignConsistency(presignedUrls: PresignedUrlResponse[], files: File[]): void {
    if (!Array.isArray(presignedUrls) || presignedUrls.length === 0) {
      throw new Error('No se recibieron URLs prefirmadas para la carga');
    }
    if (presignedUrls.length !== files.length) {
      throw new Error(
        `La cantidad de URLs prefirmadas (${presignedUrls.length}) no coincide con la cantidad de archivos (${files.length})`,
      );
    }
  }

  private cleanUrl(url: string): string {
    return (url || '').split('?')[0];
  }

  // Devuelve la primera extensión conocida cuyo MIME coincida con el entregado
  private mimeToExt(mime: string | undefined | null): string | undefined {
    if (!mime) return undefined;
    const entry = Object.entries(MIME_TYPES).find(([, v]) => v === mime);
    return entry ? entry[0] : undefined;
  }

  // Obtiene la extensión a partir del nombre del archivo; si no existe, usa el MIME como respaldo
  private getFileExtension(file: File): string {
    const name = (file?.name || '').trim();
    const dot = name.lastIndexOf('.');
    if (dot > -1 && dot < name.length - 1) {
      return name.substring(dot + 1).toLowerCase();
    }
    return this.mimeToExt(file?.type) || 'bin';
  }

  createPresignedUrl(body: PresignedUrlRequest): Observable<PresignedUrlResponse> {
    return this.httpClient.post<PCV2Request>(`${BASE_API_PCV2}/${URI_STORAGE}/prefirmar`, body).pipe(
      retry(3),
      map(({ data }: { data: PresignedUrlResponse }) => data),
    );
  }

  // TODO: Esto se debe eliminar, solo la utiliza Andres para el asunto de formulario de peritaje pero esto debe ser
  // cambiado
  createListUrls(body: PresignedUrlRequest[]): Observable<PresignedUrlResponse[]> {
    return this.httpClient
      .post<PCV2Request>(`${BASE_API_PCV2}/${URI_STORAGE}/prefirmar`, body)
      .pipe(map(({ data }: { data: PresignedUrlResponse[] }) => data));
  }

  putObjectToS3(url: string, body: FormData | File): Observable<number> {
    const COMPLETE_PROGRESS = 100;
    return this.httpClient.put(`${url}`, body, { reportProgress: true, observe: 'events' }).pipe(
      map((event) => {
        if (event.type === HttpEventType.UploadProgress) return event.loaded / event.total;
        if (event.type === HttpEventType.Response) return COMPLETE_PROGRESS;
      }),
    );
  }

  uploadObjectToS3(files: FileToStorage[]) {
    return files.map((doc) => {
      const { url, file } = doc;
      return this.httpClient.put(`${url}`, file).pipe(map(() => 200));
    });
  }

  getObjectBase64ToS3ToDownload(attachment: Attachment): Promise<void> {
    const { path, key, ext } = attachment;
    const endpoint = `${URI_BUCKET_S3}/${path}/${key}`;
    return this.httpClient
      .get<any>(`${BASE_API_PCV2}/${URI_STORAGE}/archivosbase/${endpoint}`)
      .pipe(tap((base64) => this.downloadFile(base64, ext)))
      .toPromise()
      .catch((error) =>
        console.error(
          'Ocurrió un error mientras se descargaba el archivo: Sv:StorageService Fn:getObjectBase64ToS3',
          error,
        ),
      );
  }

  getObjectBase64ToS3(attachment: Attachment): Observable<string> {
    const { path, key, ext } = attachment;
    const endpoint = `${URI_BUCKET_S3}/${path}/${key}`;
    return this.httpClient.get<any>(`${BASE_API_PCV2}/${URI_STORAGE}/archivosbase/${endpoint}`);
  }

  downloadFile(base64: string, extension: string): void {
    extension = extension.replace('.', '');
    const contentType = MIME_TYPES[extension];
    const blob = b64toBlob.default(base64, contentType);
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, '_blank');
  }

  getParamsToRequestSignedUrl(requestFiles: FileToStorage[]): Observable<PresignedUrlResponse>[] {
    return requestFiles.map((file) => {
      const { extension, path } = file;
      const presigned: PresignedUrlRequest = {
        path,
        ext: extension,
        bucket: URI_BUCKET_S3,
      };
      return this.createPresignedUrl(presigned);
    });
  }

  async presignedUrlToAttachments(fileToStorages: FileToStorage[]): Promise<Attachment[]> {
    const datetime = await DateService.getTime('datetime');
    return fileToStorages.map((presigned) => ({
      key: presigned.key,
      ext: presigned.extension,
      path: presigned.path,
      bucket: presigned.bucket,
      category: presigned.title,
      isAttached: presigned.isAttached,
      uploadDate: datetime,
    }));
  }

  uploadRawFilesToS3(attachments: File[], formValues: any): Observable<any> {
    const DEFAULT_PATH_S3 = 'temporal';

    const extensions = attachments.map((f) => this.getFileExtension(f));

    const body: PresignedUrlsRequest = {
      bucket: URI_BUCKET_S3,
      path: DEFAULT_PATH_S3,
      ext: extensions,
    };

    return this.httpClient.post<PCV2Request>(`${BASE_API_PCV2}/${URI_STORAGE}/prefirmar`, body).pipe(
      retry(3),
      map((resp) => resp.data),
      tap((presigned) => {
        this.assertPresignConsistency(presigned, attachments);
      }),

      mergeMap((presigned) =>
        forkJoin(
          presigned.map((p: PresignedUrlResponse, i: number) => {
            if (!p?.url) throw new Error('Faltó URL prefirmada');
            
            return this.httpClient
              .put(p.url, attachments[i], {
                headers: { 'Content-Type': attachments[i].type || 'application/octet-stream' },
                observe: 'response',
                responseType: 'text'
              })
              .pipe(
                take(1),
                catchError((error) => {
                  console.error(`❌ Error en carga de archivo ${i}:`, error);
                  return throwError(() => error);
                }),
                map(() => ({
                  key: p.key,
                  path: p.path || DEFAULT_PATH_S3,
                  bucket: p.bucket || URI_BUCKET_S3,
                //  url: this.cleanUrl(p.url),
                })),
              );
          }),
        ),
      ),
      switchMap((uploadedFiles) => {
        const finalPayload = {
          ...formValues,
          attachments: uploadedFiles,
        };
        return this.uploadService.uploadDocuments(finalPayload);
      }),

      finalize(() => console.log('✔ Proceso completo de upload finalizado')),
    );
  }

  /////// DAU ///////

  getObjectBase64ToS3DAU(attachment: Attachment): Promise<string> {
    const { path, key, ext } = attachment;
    const endpoint = `${URI_BUCKET_S3}/${path}/${key}`;
    return this.httpClient
      .get<any>(`${BASE_API_PCV2}/${URI_STORAGE}/archivosbase/${endpoint}`)
      .pipe(retry(3))
      .toPromise();
  }
}
